import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  Collapse,
  Button,
} from '@mui/material';
import {
  Send as SendIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Psychology as AIIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { agentApi } from '../../services/api';
import DynamicChart from '../shared/DynamicChart';

function AISidebar({ companyId, companyName }) {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [expandedCitations, setExpandedCitations] = useState({});
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  const isGlobalMode = !companyId;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  const handleAsk = async (q = question) => {
    if (!q.trim()) return;

    const userMessage = { type: 'user', content: q };
    setConversation((prev) => [...prev, userMessage]);
    setQuestion('');
    setLoading(true);
    setError(null);

    try {
      const payload = {
        question: q,
        conversation_id: conversationId,
        ...(companyId && { company_id: companyId }),
      };

      const response = isGlobalMode
        ? await agentApi.ask(payload)
        : await agentApi.askAboutCompany(companyId, payload);

      if (response.data.conversation_id) {
        setConversationId(response.data.conversation_id);
      }

      const aiMessage = {
        type: 'ai',
        content: response.data.answer,
        citations: response.data.citations || [],
        confidence: response.data.confidence,
        chartSpecs: response.data.chart_specs || [],
        documentUrl: response.data.document_url || null,
        model: response.data.model,
      };

      setConversation((prev) => [...prev, aiMessage]);
    } catch (err) {
      console.error(err);
      let errMsg = 'Failed to get response.';
      if (err.response?.status === 429) {
        errMsg = 'Too many requests — please wait a moment before asking again.';
      } else if (err.response?.status >= 500) {
        errMsg = 'Server error — please try again.';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errMsg = 'Request timed out — please try again.';
      } else if (err.response?.data?.error) {
        errMsg = err.response.data.error;
      }
      setError(errMsg);
      // Remove the user message on failure so it doesn't clutter conversation
      setConversation((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  const toggleCitations = (index) => {
    setExpandedCitations((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight="medium">
            Art
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Biotech Intelligence
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {isGlobalMode ? 'Ask about any company, pipeline, trial, or filing' : `Focused on ${companyName}`}
        </Typography>
      </Box>

      {/* Conversation */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
        {conversation.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AIIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Hi, I'm Art.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              I can query our database, search SEC filings, analyze clinical trials, check StockTwits sentiment, and find Reddit discussions.
            </Typography>
          </Box>
        )}

        {conversation.map((msg, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            {msg.type === 'user' ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Paper
                  sx={{
                    p: 1.5,
                    maxWidth: '90%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderRadius: '12px 12px 4px 12px',
                  }}
                >
                  <Typography variant="body2">{msg.content}</Typography>
                </Paper>
              </Box>
            ) : (
              <Box>
                <Paper
                  sx={{
                    p: 1.5,
                    bgcolor: 'white',
                    borderRadius: '12px 12px 12px 4px',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                    {msg.content}
                  </Typography>

                  {/* Citations */}
                  {msg.citations && msg.citations.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        size="small"
                        label={`${msg.citations.length} sources`}
                        icon={expandedCitations[index] ? <CollapseIcon /> : <ExpandIcon />}
                        onClick={() => toggleCitations(index)}
                        sx={{ fontSize: '0.7rem' }}
                      />

                      <Collapse in={expandedCitations[index]}>
                        <Box sx={{ mt: 1 }}>
                          {msg.citations.map((citation, i) => (
                            <Box
                              key={i}
                              sx={{
                                p: 1,
                                mb: 0.5,
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                borderLeft: '2px solid',
                                borderColor: 'primary.main',
                              }}
                            >
                              <Typography variant="caption" color="primary" fontWeight="medium">
                                {citation.source_type}
                              </Typography>
                              {citation.source_date && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {citation.source_date}
                                </Typography>
                              )}
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                              >
                                {citation.excerpt?.substring(0, 150)}...
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* Charts */}
                  {msg.chartSpecs && msg.chartSpecs.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      {msg.chartSpecs.map((chartSpec, chartIndex) => (
                        <Box key={chartIndex} sx={{ mb: 1 }}>
                          <DynamicChart chartSpec={chartSpec} compact={true} />
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Document Download */}
                  {msg.documentUrl && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        href={msg.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                      >
                        Download Report
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Box>
            )}
          </Box>
        ))}

        {loading && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Analyzing...
            </Typography>
          </Box>
        )}

        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Ask a question..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
          />
          <IconButton
            color="primary"
            onClick={() => handleAsk()}
            disabled={!question.trim() || loading}
            size="small"
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
}

export default AISidebar;
